import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Collapse,
  InputAdornment,
} from "@mui/material";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import DrawIcon from "@mui/icons-material/Draw";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import DrawingAnswerPad from "../MockExams/components/DrawingAnswerPad";
import { MEASUREMENT_MODES } from "../../../utils/measurementQuestionDetect";
import { useMeasurementTools } from "../../../components/measurement/MeasurementToolsContext";

const MATH_SYMBOLS_PRIMARY = [
  "+",
  "−",
  "×",
  "÷",
  "=",
  "%",
  "°",
  "²",
  "³",
  "⁴",
  "√",
  "∛",
  "^",
  "(",
  ")",
  "½",
  "¼",
  "¾",
  "⅓",
  "⅔",
  "π",
  "±",
  "≠",
  "≈",
  "∝",
  "∴",
  "∞",
  "≤",
  "≥",
  "<",
  ">",
  "£",
];
const MATH_SHAPE_SYMBOLS = [
  { label: "□", title: "square" },
  { label: "○", title: "circle" },
  { label: "☆", title: "star" },
  { label: "△", title: "triangle" },
];
const MATH_SYMBOLS_EXTENDED = [
  "⁰",
  "¹",
  "⁵",
  "⁶",
  "⁷",
  "⁸",
  "⁹",
  "∜",
  "⅛",
  "⅜",
  "⅝",
  "⅞",
  "₀",
  "₁",
  "₂",
  "₃",
  "₄",
  "₅",
  "₆",
  "₇",
  "₈",
  "₉",
  "≡",
  "α",
  "β",
  "γ",
  "θ",
  "Δ",
  "Σ",
  "∈",
  "∉",
  "∪",
  "∩",
  "⊂",
  "⊃",
  "∅",
  "∠",
  "⊥",
  "∥",
  "→",
  "↔",
  "∫",
  "∑",
  "∏",
  "∂",
  "ℕ",
  "ℤ",
  "ℚ",
  "ℝ",
  "[",
  "]",
  "{",
  "}",
  ":",
  "·",
  "∵",
  "′",
  "″",
];
const MATH_TEMPLATES = [
  { tplLabel: "xⁿ", before: "^(", after: ")", title: "Exponent — e.g. x^(3/2)" },
  { tplLabel: "√( )", before: "√(", after: ")", title: "Square root — √(...)" },
  { tplLabel: "( )/( )", before: "(", after: ")/()", title: "Fraction — (numerator)/(denominator)" },
  { tplLabel: "a b/c", before: " (", after: "/)", title: "Mixed number — e.g. 3 (1/4)" },
];
const UNIT_SHORTCUTS = [
  { unitLabel: "cm²", value: "cm²", title: "Square centimetres" },
  { unitLabel: "cm³", value: "cm³", title: "Cubic centimetres" },
  { unitLabel: "m²", value: "m²", title: "Square metres" },
  { unitLabel: "km²", value: "km²", title: "Square kilometres" },
  { unitLabel: "ml", value: "ml", title: "Millilitres" },
  { unitLabel: "kg", value: "kg", title: "Kilograms" },
  { unitLabel: "km/h", value: "km/h", title: "Kilometres per hour" },
  { unitLabel: "m/s", value: "m/s", title: "Metres per second" },
];

/** True when a past-paper text answer is empty (string or { text, drawing }). */
export function isPastPaperTextAnswerEmpty(val) {
  if (val === null || val === undefined || val === "") return true;
  if (typeof val === "string") return val.trim() === "";
  if (typeof val === "object" && !Array.isArray(val)) {
    const t = (val.text ?? "").trim();
    const d = val.drawing;
    return t === "" && !d;
  }
  return false;
}

export function MathInputTools({ getInput, getValue, onChange, defaultOpen = false }) {
  const [toolsOpen, setToolsOpen] = useState(defaultOpen);
  const [symbolsExpanded, setSymbolsExpanded] = useState(false);

  const insertAt = (before, after = "") => {
    const input = getInput?.();
    if (!input) return;
    const textValue = getValue?.() ?? "";
    const start = input.selectionStart ?? textValue.length;
    const end = input.selectionEnd ?? textValue.length;
    const existing = textValue.slice(start, end);
    const newText = textValue.slice(0, start) + before + existing + after + textValue.slice(end);
    onChange?.(newText);
    setTimeout(() => {
      const cursorPos = start + before.length + existing.length;
      input.setSelectionRange(cursorPos, cursorPos);
      input.focus();
    }, 0);
  };

  const insertSymbol = (sym) => insertAt(sym, "");

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<KeyboardIcon fontSize="small" />}
        endIcon={toolsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        onClick={() => setToolsOpen((v) => !v)}
        sx={{ mb: 1, textTransform: "none", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.75rem" }, py: { xs: 0.75, sm: 0.5 } }}
      >
        {toolsOpen ? "Hide Math Tools" : "Show Math Tools"}
      </Button>
      <Collapse in={toolsOpen}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, alignItems: "center" }}>
          {MATH_SYMBOLS_PRIMARY.map((sym) => (
            <Button
              key={sym}
              size="small"
              variant="outlined"
              onClick={() => insertSymbol(sym)}
              sx={{ minWidth: 32, p: 0.5, fontSize: "0.9rem" }}
            >
              {sym}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, alignItems: "center" }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setSymbolsExpanded((e) => !e)}
            sx={{ fontSize: "0.75rem", textTransform: "none", color: "primary.main" }}
          >
            {symbolsExpanded ? "Fewer symbols" : "More symbols"}
          </Button>
        </Box>
        {symbolsExpanded && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mb: 1,
              pl: 0.5,
              borderLeft: "2px solid",
              borderColor: "divider",
            }}
          >
            {MATH_SYMBOLS_EXTENDED.map((sym) => (
              <Button
                key={sym}
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => insertSymbol(sym)}
                sx={{ minWidth: 32, p: 0.5, fontSize: "0.9rem" }}
              >
                {sym}
              </Button>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600, minWidth: 64 }}>
            Shapes:
          </Typography>
          {MATH_SHAPE_SYMBOLS.map(({ label, title }) => (
            <Button
              key={label}
              size="small"
              variant="outlined"
              color="secondary"
              title={title}
              onClick={() => insertSymbol(label)}
              sx={{ minWidth: 36, p: 0.5, fontSize: "1rem", fontFamily: "monospace" }}
            >
              {label}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600, minWidth: 64 }}>
            Templates:
          </Typography>
          {MATH_TEMPLATES.map(({ tplLabel, before, after, title }) => (
            <Button
              key={tplLabel}
              size="small"
              variant="outlined"
              color="success"
              title={title}
              onClick={() => insertAt(before, after)}
              sx={{ minWidth: 52, p: 0.5, fontSize: "0.82rem", fontFamily: "monospace" }}
            >
              {tplLabel}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600, minWidth: 64 }}>
            Units:
          </Typography>
          {UNIT_SHORTCUTS.map(({ unitLabel, value, title }) => (
            <Button
              key={unitLabel}
              size="small"
              variant="outlined"
              color="warning"
              title={title}
              onClick={() => insertSymbol(value)}
              sx={{ minWidth: 40, p: 0.5, fontSize: "0.78rem", fontFamily: "monospace" }}
            >
              {unitLabel}
            </Button>
          ))}
        </Box>
      </Collapse>
    </>
  );
}

/**
 * Tabbed type + draw/write working (same UX as mock exams).
 * answer: string | { text: string, drawing: string | null }
 */
export function TypeDrawInput({ answer, onChange, multiline, rows, label, questionIdx, marks, checkAnswerButton, defaultTab, drawLabel, measurementMode, showMathTools = true, contextImageUrl, pendingFloatImage, onFloatImageConsumed, questionImages, autoInsertFloating = false, autoLoadQuestionDiagram = false, unitSuffix, unitPrefix, answerLength, allowDraw = true }) {
  const textInputRef = useRef(null);
  const wrapperRef = useRef(null);
  const textValue = answer && typeof answer === "object" ? answer.text ?? "" : answer ?? "";
  const drawingValue = answer && typeof answer === "object" ? answer.drawing ?? null : null;
  // The unit is stored joined to the value ("3 1/2 hours") but shown as an adornment chip, so the
  // editable field holds just the value. Strip the suffix/prefix and the ONE separator space we add —
  // but never trim the value itself, or a space the student is mid-typing (e.g. between "3" and "1/2")
  // would be deleted on every keystroke, making it impossible to type spaces in a unit field.
  const displayValue = (() => {
    if (unitSuffix && textValue.endsWith(unitSuffix)) {
      const base = textValue.slice(0, -unitSuffix.length);
      return base.endsWith(" ") ? base.slice(0, -1) : base;
    }
    if (unitPrefix && textValue.startsWith(unitPrefix)) {
      const base = textValue.slice(unitPrefix.length);
      return base.startsWith(" ") ? base.slice(1) : base;
    }
    return textValue;
  })();

  const compactLen = !multiline && Number.isFinite(answerLength) && answerLength > 0 ? answerLength : null;
  const unitPad = (unitPrefix || unitSuffix) ? (String(unitPrefix || unitSuffix).length + 3) : 0;
  const compactCh = compactLen ? Math.min(Math.max(Math.ceil(compactLen) + 8 + unitPad, 12), 36) : null;

  const [tab, setTab] = useState(() => {
    if (!allowDraw) return 0;
    return drawingValue ? 1 : (defaultTab ?? 0);
  });
  const measurementTools = useMeasurementTools();
  const hasGeometryTools = Boolean(measurementTools?.measurementMode || measurementMode);

  // After switching to the draw tab, re-anchor the view to the top of this widget.
  // Without this, the browser auto-scrolls to the newly-mounted canvas element and jumps
  // the page to the middle of the screen.
  const switchToDrawTab = () => {
    if (!allowDraw) return;
    setTab(1);
    requestAnimationFrame(() => {
      wrapperRef.current?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  };

  useEffect(() => {
    if (!allowDraw && tab !== 0) setTab(0);
  }, [allowDraw, tab]);

  const prevDrawingRef = useRef(drawingValue);
  useEffect(() => {
    if (!allowDraw) return;
    if (drawingValue && !prevDrawingRef.current) switchToDrawTab();
    prevDrawingRef.current = drawingValue;
  }, [drawingValue, allowDraw]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!allowDraw) return;
    if (pendingFloatImage) switchToDrawTab();
  }, [pendingFloatImage, allowDraw]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTextChange = (text) => {
    // Join the unit with a single space ("3 1/2" + "hours" → "3 1/2 hours") and keep an empty answer
    // empty. The display strip above removes exactly this separator, so spaces round-trip cleanly.
    const fullText = unitSuffix
      ? (text ? `${text} ${unitSuffix}` : "")
      : unitPrefix
        ? (text ? `${unitPrefix} ${text}` : "")
        : text;
    if (drawingValue) {
      onChange({ text: fullText, drawing: drawingValue });
    } else {
      onChange(fullText || "");
    }
  };

  const handleDrawingChange = (dataUrlOrObj) => {
    const currentText = textValue;
    if (dataUrlOrObj) {
      const isTagged = dataUrlOrObj && typeof dataUrlOrObj === "object" && dataUrlOrObj.diagramOnCanvas;
      const drawingData = isTagged ? dataUrlOrObj.dataUrl : dataUrlOrObj;
      onChange({ text: currentText, drawing: drawingData, ...(isTagged ? { diagramOnCanvas: true } : {}) });
    } else if (currentText) {
      onChange(currentText);
    } else {
      onChange("");
    }
  };

  return (
    <Box ref={wrapperRef}>
      {/* Tabs row: full-width on mobile so no scroll arrows; check answer button sits below on mobile */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: { xs: 0.75, sm: 0 }, mb: 1 }}>
        {allowDraw ? (
          <Tabs
            value={tab}
            onChange={(_, v) => v === 1 ? switchToDrawTab() : setTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              flex: 1,
              minWidth: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              "& .MuiTab-root": { minHeight: 40, py: 0.5, textTransform: "none", fontWeight: 600, fontSize: { xs: "0.9rem", sm: "0.82rem" } },
              "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            <Tab icon={<KeyboardIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Type Answer" />
            <Tab icon={<DrawIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={drawLabel ?? "Draw Answer"} />
          </Tabs>
        ) : null}
        {checkAnswerButton && (
          <Box sx={{ display: "flex", justifyContent: { xs: "flex-end", sm: "auto" }, ml: { sm: allowDraw ? 1 : 0 }, width: allowDraw ? "auto" : "100%" }}>
            {checkAnswerButton}
          </Box>
        )}
      </Box>

      {tab === 0 && (
        <Box>
          {hasGeometryTools ? (
            <Typography variant="caption" color="primary.main" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
              {(measurementTools?.measurementMode || measurementMode) === MEASUREMENT_MODES.ANGLE
                ? "Use the Show protractor button at the top, measure on the diagram, then type degrees (e.g. 45°)."
                : (measurementTools?.measurementMode || measurementMode) === MEASUREMENT_MODES.LENGTH
                ? "Use the Show ruler button at the top, measure on the diagram, then type the length (e.g. 6.2 cm)."
                : "Use the Show protractor / Show ruler buttons at the top of the question, measure on the diagram, then type your answer."}
            </Typography>
          ) : null}
          {showMathTools ? (
            <MathInputTools
              getInput={() => textInputRef.current}
              getValue={() => displayValue}
              onChange={handleTextChange}
              defaultOpen={hasGeometryTools}
            />
          ) : null}
          <TextField
            inputRef={textInputRef}
            fullWidth={!compactCh}
            sx={compactCh ? { width: `${compactCh}ch`, maxWidth: "100%" } : undefined}
            placeholder={compactCh ? "Answer" : "Type your answer here..."}
            value={displayValue}
            onChange={(e) => handleTextChange(e.target.value)}
            multiline={multiline}
            rows={rows}
            label={label}
            inputProps={{ style: { fontSize: "1rem" } }}
            InputLabelProps={{ style: { fontSize: "1rem" } }}
            InputProps={{
              ...(unitPrefix ? {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", userSelect: "none" }}>
                      {unitPrefix}
                    </Typography>
                  </InputAdornment>
                ),
              } : {}),
              ...(unitSuffix ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", userSelect: "none" }}>
                      {unitSuffix}
                    </Typography>
                  </InputAdornment>
                ),
              } : {}),
            }}
          />
        </Box>
      )}

      {allowDraw && tab === 1 && (
        <Box>
          <DrawingAnswerPad
            questionIdx={questionIdx}
            onChange={handleDrawingChange}
            minHeight={560}
            label={drawLabel ?? "Your Answer (Drawing)"}
            initialDrawing={drawingValue}
            marks={marks}
            contextImageUrl={contextImageUrl}
            pendingFloatImage={pendingFloatImage}
            onFloatImageConsumed={onFloatImageConsumed}
            questionImages={questionImages}
            autoInsertFloating={autoInsertFloating}
            autoLoadQuestionDiagram={autoLoadQuestionDiagram}
          />
          {drawingValue && (
            <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block", fontWeight: 600 }}>
              Drawing saved — will be submitted with your answer.
            </Typography>
          )}
        </Box>
      )}

      {allowDraw && textValue && textValue.trim() !== "" && drawingValue && (
        <Box
          sx={{
            mt: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.200",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="info.dark" sx={{ fontWeight: 600 }}>
            Both typed and drawn answers will be submitted with your attempt.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/** Local-only scratch pad for multiple-choice working (not submitted). */
export function ScratchPad({ questionIdx, scratchDrawingsRef, onScratchChange, marks }) {
  const [open, setOpen] = useState(false);
  const initialDrawing = scratchDrawingsRef?.current?.[questionIdx] ?? null;
  const handleChange = (dataUrl) => {
    if (scratchDrawingsRef?.current) scratchDrawingsRef.current[questionIdx] = dataUrl || null;
    onScratchChange?.(questionIdx, dataUrl);
  };
  return (
    <Box sx={{ mt: 2 }}>
      <Button
        size="small"
        variant="text"
        startIcon={open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        endIcon={<DrawIcon fontSize="small" />}
        onClick={() => setOpen((v) => !v)}
        sx={{
          textTransform: "none",
          color: "text.secondary",
          fontWeight: 600,
          fontSize: "0.8rem",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {open ? "Hide Scratch Pad" : "Show Scratch Pad"}
      </Button>
      <Collapse in={open}>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontStyle: "italic" }}>
            Use this pad for rough working — it is NOT submitted with your answer.
          </Typography>
          <DrawingAnswerPad
            questionIdx={questionIdx}
            onChange={handleChange}
            minHeight={280}
            label="Scratch / Working"
            initialDrawing={initialDrawing}
            marks={marks}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
